import React, { useRef } from 'react';
import './App.css';
// View-split styles (import after App.css so they override it)
import './styles/base.css';
import './styles/max-900.css';
import './styles/max-600.css';
import './styles/max-425.css';
import './styles/min-1024.css';
import NavMenu from './NavMenu';
import Slideshow from './Slideshow';
import FeaturedVideo from './FeaturedVideo';
import LoginPage from './LoginPage';
import UserSettingsPage from './UserSettingsPage';
import BlogPage from './BlogPage';

function App() {
  const topOfPageRef = useRef(null);
  // Nav menu is fully handled in `NavMenu` component
  const pathname = window.location.pathname.toLowerCase();
  const isAuthRoute = pathname === '/login' || pathname === '/login/' || pathname === '/register' || pathname === '/register/';
  const isSettingsRoute = pathname === '/settings' || pathname === '/settings/';
  const isBlogRoute = pathname === '/blog' || pathname === '/blog/';
  const initialAuthMode = pathname.startsWith('/register') ? 'register' : 'login';

  function scrollToTop() {
    topOfPageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }

  if (isAuthRoute) {
    return <LoginPage initialMode={initialAuthMode} />;
  }

  if (isSettingsRoute) {
    return <UserSettingsPage />;
  }

  return (
    <div className="nav-container">
      <div ref={topOfPageRef} aria-hidden="true" />
      <NavMenu />
      {/* Mobile dropdown is rendered inside `NavMenu` component now */}
      {
        <>
          <div className="main-container">
            {isBlogRoute ? <BlogPage /> : <Slideshow alwaysShowText={true} />}
          </div>
          {!isBlogRoute && (
            <div className="main-content-row">
              <section className="youtube-section">
                <FeaturedVideo />
              </section>
              <section className="about-container">
                <div className="about-title">About Me</div>
                <div className="about-content">
                  <img className="about-photo" src="/img/Profile_Photo.jpg" alt="Derek Downs"/>
                  <div className="about-text">
                    <p>
                      Hi, I'm Derek Downs. Welcome to my modern portfolio and blog!<br />
                      I'm a CS student at ASU focused on Cybersecurity, passionate about LEGO, Paintball, Bodybuilding, Cars, and Tech.<br />
                      This site is a living hub for my projects, interests, and business. If something looks unfinished, it's probably in development!
                    </p>
                  </div>
                </div>
              </section>
            </div>
          )}
        </>
      }
      <footer className="footer">
        <div className="container">
          <div className="footer-logo">
            <button type="button" className="logo-button" onClick={scrollToTop}>
              <img src="/img/resumeLogo.png" alt="Logo" width="200px" />
            </button>
          </div>
          <div className="footer-social-media">
            <a target="_blank" rel="noopener noreferrer" href="https://www.facebook.com/derek.downs.1614">
              {/* SVG Facebook icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25"><title>Facebook</title><path fill="#FFF" d="M18.896 0H1.104C.494 0 0 .494 0 1.104v17.793C0 19.506.494 20 1.104 20h9.58v-7.745H8.076V9.237h2.606V7.01c0-2.583 1.578-3.99 3.883-3.99 1.104 0 2.052.082 2.329.119v2.7h-1.598c-1.254 0-1.496.597-1.496 1.47v1.928h2.989l-.39 3.018h-2.6V20h5.098c.608 0 1.102-.494 1.102-1.104V1.104C20 .494 19.506 0 18.896 0z" /></svg>
            </a>
            <a target="_blank" rel="noopener noreferrer" href="https://www.youtube.com/@C4Cookiie">
              {/* SVG YouTube icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25"><title>Youtube</title><path fill="#FFF" d="M10.333 0c-5.522 0-10 4.478-10 10 0 5.523 4.478 10 10 10 5.523 0 10-4.477 10-10 0-5.522-4.477-10-10-10zm3.701 14.077c-1.752.12-5.653.12-7.402 0C4.735 13.947 4.514 13.018 4.5 10c.014-3.024.237-3.947 2.132-4.077 1.749-.12 5.651-.12 7.402 0 1.898.13 2.118 1.059 2.133 4.077-.015 3.024-.238 3.947-2.133 4.077zM8.667 8.048l4.097 1.949-4.097 1.955V8.048z" /></svg>
            </a>
            <a target="_blank" rel="noopener noreferrer" href="https://x.com/C4Cookiie">
              {/* SVG Twitter/X icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25"><title>Twitter/X</title><path fill="#FFF" d="M20.667 2.797a8.192 8.192 0 01-2.357.646 4.11 4.11 0 001.804-2.27 8.22 8.22 0 01-2.606.996A4.096 4.096 0 0014.513.873c-2.649 0-4.595 2.472-3.997 5.038a11.648 11.648 0 01-8.457-4.287 4.109 4.109 0 001.27 5.478A4.086 4.086 0 011.47 6.59c-.045 1.901 1.317 3.68 3.29 4.075a4.113 4.113 0 01-1.853.07 4.106 4.106 0 003.834 2.85 8.25 8.25 0 01-6.075 1.7 11.616 11.616 0 006.29 1.843c7.618 0 11.922-6.434 11.662-12.205a8.354 8.354 0 002.048-2.124z" /></svg>
            </a>
            <a target="_blank" rel="noopener noreferrer" href="https://www.instagram.com/realderekdowns/">
              {/* SVG Instagram icon */}
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25"><title>Instagram</title><path fill="#FFF" d="M10.333 0c-5.522 0-10 4.478-10 10 0 5.523 4.478 10 10 10 5.523 0 10-4.477 10-10 0-5.522-4.477-10-10-10zm0 18.52a8.52 8.52 0 110-17.04 8.52 8.52 0 010 17.04zm5.185-14.71a1.996 1.996 0 100 3.992 1.996 1.996 0 000-3.992z" /></svg>
            </a>
          </div>
          <div className="footer-links col1">
            <a href="/">Home</a>
            <a href="/blog">Blog</a>
            <a href="/contact">Contact</a>
          </div>
          <div className="footer-links col2">
            <a href="https://store.bricklink.com/DerekDowns#/shop" target="_blank" rel="noopener noreferrer">BrickLink</a>
            <a href="https://linktr.ee/C4Cookiie" target="_blank" rel="noopener noreferrer">LinkTree</a>
            <button type="button" className="footer-link-button">Privacy Policy</button>
          </div>
          <div className="footer-cta">
            <button type="button" className="button" onClick={scrollToTop}>Back To Top</button>
          </div>
          <div className="footer-copyright">
            <p>© 2025 Derek Downs. All Rights Reserved</p>
          </div>
        </div>
        <div className="attribution">
          <span className="outer-link">Full stack development by Derek Downs</span>
          <ul className="footer-github">
            <li>
              <a target="_blank" rel="noopener noreferrer" href="https://github.com/DerekMDowns" title="Visite My Github Account">
                <img src="https://raw.githubusercontent.com/devicons/devicon/1a5dbfa142a9677d6ddaa05d05f377ac23af9a2e/icons/github/github-original.svg" className="light" alt="My Github Account" />
              </a>
            </li>
          </ul>
        </div>
      </footer>
    </div>
  );
}

export default App;
